'use client';

import { Card, CardContent, Chip, Typography, Button, Box, Stack } from '@mui/material';
import { Calendar, DollarSign } from 'lucide-react';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';

interface Job {
    id: string;
    title: string;
    company: string;
    status: 'active' | 'completed' | 'pending';
    budget: string;
    deadline: string;
    description: string;
}

interface JobListProps {
    jobs: Job[];
    onSeeDetails: (jobId: string) => void;
}

export function JobList({ jobs, onSeeDetails }: JobListProps) {
    const { themeStyle } = useThemeContext();
    const cardBg = theme[themeStyle].secondaryBg;
    const textColor = theme[themeStyle].tag_white;
    const currentTheme = theme[themeStyle];

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return { backgroundColor: '#dcfce7', color: '#15803d' }; // green
            case 'completed':
                return { backgroundColor: '#dbeafe', color: '#1e40af' }; // blue
            case 'pending':
                return { backgroundColor: '#fef9c3', color: '#92400e' }; // yellow
            default:
                return { backgroundColor: '#f3f4f6', color: '#374151' }; // gray
        }
    };

    return (
        <Stack spacing={2}>
            {jobs.map(job => (
                <Card
                    key={job.id}
                    sx={{ backgroundColor: cardBg, border: `0.5px solid ${currentTheme.deep_grey}` }}
                    elevation={0}>
                    <CardContent sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Typography variant="h6" fontWeight={600} sx={{ color: textColor }}>
                                        {job.title}
                                    </Typography>
                                    <Chip
                                        label={job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                                        sx={{
                                            height: 24,
                                            fontSize: '0.75rem',
                                            ...getStatusColor(job.status),
                                        }}
                                    />
                                </Box>

                                <Typography variant="body2" sx={{ color: textColor }} mb={0.5}>
                                    {job.company}
                                </Typography>
                                <Typography variant="body2" sx={{ color: textColor }} mb={2}>
                                    {job.description}
                                </Typography>

                                <Stack
                                    direction="row"
                                    spacing={3}
                                    alignItems="center"
                                    fontSize="0.875rem"
                                    sx={{ color: textColor }}>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <DollarSign size={16} style={{ color: textColor }} />
                                        <span>{job.budget}</span>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Calendar size={16} style={{ color: textColor }} />
                                        <span>{job.deadline}</span>
                                    </Box>
                                </Stack>
                            </Box>

                            <Button
                                variant="outlined"
                                sx={{
                                    ml: 2,
                                    mt: 1,
                                    whiteSpace: 'nowrap',
                                    color: textColor,
                                    borderColor: textColor,
                                    '&:hover': {
                                        backgroundColor: textColor,
                                        color: cardBg,
                                    },
                                }}
                                onClick={() => onSeeDetails(job.id)}>
                                See Details
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            ))}
        </Stack>
    );
}
